// Shared overlay accessibility for the modals: Escape-to-close, a focus trap
// (Tab/Shift+Tab cycle within the dialog), and focus restore on close.
//
// To use: add `mixins: [modalA11y]` to a modal component, and put
// `role="dialog" aria-modal="true"` (+ an aria-label) on its dialog element.
// The mixin emits the component's close event on Escape — `close` by default;
// set a top-level `closeEvent: 'dismiss'` option to override (AnnouncementModal).
// An element inside the dialog that uses Escape itself (a picker's search box closing
// its own list) opts out with a `data-own-escape` attribute on it or an ancestor.
//
// Assumes the modal is mounted/unmounted with v-if (so the lifecycle hooks map
// to open/close), which is how Luxstadio renders them.

// Open modals, oldest first. Only the topmost handles keys, so a picker opened from
// inside a modal (My Mods' class picker) closes alone on Escape and keeps Tab to itself.
const open = [];

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default {
  mounted() {
    this._prevFocus = (typeof document !== 'undefined') && document.activeElement;
    open.push(this);
    document.addEventListener('keydown', this._onModalKeydown, true);
    this.$nextTick(() => {
      const f = this._modalFocusables();
      (f[0] || this.$el)?.focus?.({ preventScroll: true });   // a tall dialog opens at its top
    });
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this._onModalKeydown, true);
    const i = open.indexOf(this);
    if (i >= 0) open.splice(i, 1);
    if (this._prevFocus && this._prevFocus.focus) this._prevFocus.focus();
  },
  methods: {
    _modalFocusables() {
      if (!this.$el || !this.$el.querySelectorAll) return [];
      return [...this.$el.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
    },
    _onModalKeydown(e) {
      if (open[open.length - 1] !== this) return;
      if (e.key === 'Escape') {
        if (e.target && e.target.closest && e.target.closest('[data-own-escape]')) return;
        e.stopPropagation();
        this.$emit(this.$options.closeEvent || 'close');
        return;
      }
      if (e.key !== 'Tab') return;
      const f = this._modalFocusables();
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    },
  },
};
